(function(window, scormControllerFactory) {
   'use strict';

   var namespace = window.TSC = window.TSC || {};
   namespace.scormController = scormControllerFactory(window, namespace.scormConstants, namespace.ScormDataModel, namespace.ScormApiWrapper, namespace.videoAnalytics);
})(window, function(window, scormConstants, ScormDataModel, ScormApiWrapper, videoAnalytics) {
   'use strict';

   var DEFAULT_PASSING_SCORE = 80;
   var DEFAULT_VIDEO_WATCH_PERCENT_FOR_COMPLETE = 1;
   var REPORT_INCREMENT = 0.025;

   /**
    * @memberof TSC
    * @class scormController
    * @static
    * @classdesc Scorm Controller
    */
   var scormController = function() {
      var _minimumScoreToPass = DEFAULT_PASSING_SCORE;
      var _minimumViewPercentageToCompleteLesson = DEFAULT_VIDEO_WATCH_PERCENT_FOR_COMPLETE;
      var _scormApiWrapper;
      var _isDisposed;
      var _initialized;
      var _scormModel;
      var _previousWriteVideoTime;
      var _quizModel = null;

      var initializeInternalState = function() {
         _scormApiWrapper = ScormApiWrapper.create();
         _isDisposed = false;
         _initialized = false;
         _scormModel = ScormDataModel.create();
         _previousWriteVideoTime = 0;
      };

      /**
       * Submit percentage of video watched to SCORM api
       * @function submitVideoWatchedPercentage
       * @memberof TSC.scormController
       * @param {Number} percentageWatched - Percentage of video watched from 0 - 1
       * @param {Number} timeInSeconds - Current time of video in seconds
       */
      var submitVideoWatchedPercentage = function(percentageWatched, timeInSeconds) {
         // Note: the percentage reported via value reports how much was actually watched
         //       and not the percentage of the timeline that the playhead is at.
         //       So, for example, if you started up a video and then immediately seeked to
         //       the 75% pt, the values reported here would still start from zero.
         _scormModel.percentageComplete = Math.max(0, Math.min(1, percentageWatched));
         _scormModel.videoTimeInSeconds = timeInSeconds;

         if (_scormModel.percentageComplete >= _minimumViewPercentageToCompleteLesson) {
            _scormModel.completionStatus = scormConstants.status.completed;
         }

         // This gets called a lot, so we don't want to update the LMS every time.
         // We'll arbitrarily choose 2.5% increments
         if (_scormModel.percentageComplete - _previousWriteVideoTime >= REPORT_INCREMENT) {
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiProgressMeasure, _scormModel.percentageComplete);
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiSuspendData, getSuspendData());
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiLocation, _scormModel.videoTimeInSeconds);
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiCompletionStatus, _scormModel.completionStatus);
            _scormApiWrapper.apiCall(scormConstants.fn.commit);

            _previousWriteVideoTime = _scormModel.percentageComplete;
         }
      };

      /**
       * Submit a score to the SCORM api
       * @memberof TSC.scormController
       * @function submitScore
       * @param {Number} currentScore - Score to report to Scorm
       * @param {Boolean} allQuestionsAnswered - Are all questions answered
       */
      var submitScore = function(currentScore, allQuestionsAnswered) {
         var scoreNeededToPass = _minimumScoreToPass;

         if (!isNaN(currentScore)) {
            var scoreNeededMultiplier = null;
            var maxPassingScoreValue = null;
            var minScore = 0;
            var maxScore = 100;

            if (_scormApiWrapper.apiVersion === scormConstants.versions.SCORM_2004) {
               scoreNeededMultiplier = 100;
               maxPassingScoreValue = 1;
            } else if (_scormApiWrapper.apiVersion === scormConstants.versions.SCORM_1_2) {
               scoreNeededMultiplier = 1;
               maxPassingScoreValue = 100;
            }

            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiScoreMin, minScore);
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiScoreMax, maxScore);
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiScoreRaw, currentScore);
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiScoreScaled, currentScore / maxScore);
            _scormApiWrapper.apiCall(scormConstants.fn.commit);

            // lms defined mastery level will take precedence over sco defined mastery level
            var lmsPassingScore = _scormApiWrapper.apiCall(scormConstants.fn.getValue, scormConstants.dataModelID.cmiScaledPassingScore);
            if (lmsPassingScore !== null && lmsPassingScore !== '' && lmsPassingScore >= 0 && lmsPassingScore <= maxPassingScoreValue) {
               scoreNeededToPass = lmsPassingScore * scoreNeededMultiplier;
            }

            if (allQuestionsAnswered) {
               if (currentScore >= scoreNeededToPass) {
                  _scormModel.passingStatus = scormConstants.status.passed;
               } else {
                  _scormModel.passingStatus = scormConstants.status.failed;
               }
            } else {
               _scormModel.passingStatus = scormConstants.status.unknown;
            }
         }
      };

      var getSuspendedDataFromAPI = function() {
         var suspendedData = _scormApiWrapper.apiCall(scormConstants.fn.getValue, scormConstants.dataModelID.cmiSuspendData);
         _scormModel.parseSuspendedDataString(suspendedData);
      };

      var init = function() {
         initializeInternalState();

         return _scormApiWrapper.init().then(function(apiResult) {
            if (apiResult === 'false') {
               window.alert('Failed to initialize communications with the LMS!'); // eslint-disable-line
               return;
            }

            getSuspendedDataFromAPI();

            window.onbeforeunload = pageUnload;

            _scormModel.completionStatus = scormConstants.status.incomplete;
            _initialized = true;
         });
      };

      var getSuspendData = function() {
         if (_quizModel) {
            _scormModel.quizModelSnapshot = _quizModel.getSnapShot();
         }
         _scormModel.videoAnalyticsSnapshot = videoAnalytics.getVideoSegmentSnapShot();
         return _scormModel.getSuspendedDataString();
      };

      var dispose = function() {
         if (_scormModel.percentageComplete >= _minimumViewPercentageToCompleteLesson) {
            _scormModel.completionStatus = scormConstants.status.completed;
         }

         if (!_isDisposed) {
            var timeInSeconds = videoAnalytics.getCurrentPlayTime();

            var scoExitValue = scormConstants.exitStates.complete;
            var scoCompletionStatusValue = _scormModel.completionStatus;
            var scoSuccessStatusValue = _scormModel.passingStatus;

            if (_scormModel.passingStatus === scormConstants.status.unknown) {
               // SCORM 1.2 when video has quiz and not answered all questions complete status will be 'incomplete'
               if (_scormApiWrapper.apiVersion === scormConstants.versions.SCORM_1_2 && _quizModel !== null) {
                  scoCompletionStatusValue = scormConstants.status.incomplete;
               }
               scoExitValue = scormConstants.exitStates.suspend;
            }

            // SCORM 1.2 completionStatus value can be 'incomplete', 'passed' or 'failed'
            // SCORM 1.2 does not have a key to store an independent success status.
            if (_scormApiWrapper.apiVersion === scormConstants.versions.SCORM_1_2 && _scormModel.passingStatus !== scormConstants.status.unknown && scoCompletionStatusValue === scormConstants.status.completed) {
               scoCompletionStatusValue = _scormModel.passingStatus;
               scoSuccessStatusValue = '';
            }

            var suspendedData = scoExitValue === scormConstants.exitStates.suspend ? getSuspendData() : '';
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiExit, scoExitValue);
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiSuspendData, suspendedData);
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiProgressMeasure, _scormModel.percentageComplete);
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiLocation, timeInSeconds);
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiSuccessStatus, scoSuccessStatusValue);
            _scormApiWrapper.apiCall(scormConstants.fn.setValue, scormConstants.dataModelID.cmiCompletionStatus, scoCompletionStatusValue);
            _scormApiWrapper.apiCall(scormConstants.fn.commit);
            _initialized = false;
            _isDisposed = true;
         }
      };

      var pageUnload = function() {
         window.onbeforeunload = null;
         dispose();
         _scormApiWrapper.apiCall(scormConstants.fn.terminate);
      };

      return Object.defineProperties({
         init: init,
         submitVideoWatchedPercentage: submitVideoWatchedPercentage,
         submitScore: submitScore
      }, {
         initialized: {
            get: function() {
               return _initialized;
            }
         },
         initialPercentageComplete: {
            get: function() {
               return _scormModel.percentageComplete;
            }
         },
         initialVideoAnalyticsSnapshot: {
            get: function() {
               return _scormModel.videoAnalyticsSnapshot;
            }
         },
         initialQuizModelSnapshot: {
            get: function() {
               return _scormModel.quizModelSnapshot;
            }
         },
         initialVideoTimeInSeconds: {
            get: function() {
               return _scormModel.videoTimeInSecondsSnapshot;
            }
         },
         minScoreToPassLesson: {
            get: function() {
               return _minimumScoreToPass;
            },
            set: function(minimumScoreToPass) {
               _minimumScoreToPass = minimumScoreToPass;
            }
         },
         minViewPercentageToCompleteLesson: {
            get: function() {
               return _minimumViewPercentageToCompleteLesson;
            },
            set: function(minimumViewPercentageToCompleteLesson) {
               _minimumViewPercentageToCompleteLesson = minimumViewPercentageToCompleteLesson;
            }
         },
         quizModel: {
            get: function() {
               return _quizModel;
            },
            set: function(quizModel) {
               _quizModel = quizModel;
            }
         }
      });
   };

   return scormController();
});
